<script>
import _ from "lodash";
import VDataTableCustomHeader from "@/components/VDataTable/VDataTableCustomHeader.vue";
import UserAuth from "@/mixins/UserAuth";
import VIconTooltip from "@/components/commonComponents/VIconTooltip.vue";
import BusinessProcess from "@/mixins/BusinessProcess";
import VIconFlag from "@/components/commonComponents/VIconFlag.vue";
// import TextEllipsis from "@/components/commonComponents/TextEllipsis.vue";

export default {
    name: "VDataTableCustom",
    components: {
        // TextEllipsis,
        VIconTooltip,
        VDataTableCustomHeader,
        VIconFlag
    },
    inheritAttrs: false,
    mixins: [
        UserAuth,
        BusinessProcess
    ],
    props: {
        heading: {
            type: String,
            default: "",
        },
        headers: {
            type: Array,
        },
        search: {
            type: [String, Boolean],
        },
        footerProps: {
            type: Object,
        },
        items: {
            type: Array,
        },
        itemClass: {
            type: [String, Function],
        },
        canEdit: {
            type: Function,
            default: (item) => !!item?.permissions?.canUpdate,
        },
        canDelete: {
            type: Function,
            default: (item) => !!item?.permissions?.canDelete,
        },
        filter: {
            type: Boolean,
        },
        permissionsByModule: {
            type: String,
        },
        hideDefaultHeader: {
            type: Boolean,
        },
        withToolbar: {
            type: Boolean,
        },
        sortBy: {
            type: [Array, String],
        },
        sortDesc: {
            type: [Array, Boolean],
        },
        customPermissions: {
            type: Object,
        },
        clearSort: {
            type: Boolean,
            default: false
        },
        toolbarAdd: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            searchLocal: _.isString(this.search) ? this.search : '',
            searchEnable: _.isBoolean(this.search) ? this.search : (this.search !== undefined),
            searchActive: false,
            filterLocal: false,
            filters: {},
            filteredItems: [],
            sortByLocal: this.sortBy,
            sortDescLocal: this.sortDesc,
            sortByDefault: this.sortBy,
            sortDescDefault: this.sortDesc,
        };
    },
    computed: {
        permissions() {
            return Object.assign(
                {
                    canCreate: false,
                    canUpdate: false,
                    canDelete: false,
                },
                this.user?.permissions?.byModule[this.permissionsByModule] || {},
                this.customPermissions || {}
            );
        },
        isItemsPerPageHidden() {
            return this.footerProps?.itemsPerPageOptions?.length <= 1;
        },
        headersWithActions() {
            const permissions = this.permissions,
                actionsCount = [
                    !!this.$listeners['click:delete'] && permissions.canDelete,
                    !!this.$listeners['click:edit'] && permissions.canUpdate,
                ].filter(v => v).length,
                hasActions = actionsCount > 0,
                headerActions = hasActions ? [{
                    text: '',
                    value: 'actions',
                    sortable: false,
                    width: 32 + 16 * actionsCount,  // 32 - padding, 16 - иконка
                    cellClass: 'text-no-wrap',
                }] : [];
            return this.headers.concat(headerActions);
        },
        headersWithTooltip() {
            return _(this.headers).filter(v => v.tooltip).map('value').value();
        },
        isDefaultSort() {
            return _.isEqual(this.sortByLocal, this.sortByDefault) && _.isEqual(this.sortDescLocal, this.sortDescDefault);
        },
        isNoSort() {
            return _.isEqual(this.sortByLocal, []) && _.isEqual(this.sortDescLocal, []);
        },
    },
    watch: {
        search(v) {
            this.searchLocal = _.isString(v) ? v : '';
            this.searchEnable = _.isBoolean(v) ? v : (v !== undefined);
            this.searchActive = this.searchActive || !this.searchLocal;
        },
        searchActive(v) {
            if (!v) {
                this.searchLocal = '';
            }
        },
        searchLocal(v) {
            this.$emit('update:search', v);
        },
        filterLocal(v) {
            if (!v) {
                this.filters = {};
            }
        },
    },
    methods: {
        getItemClass(item) {
            return [
                _.isFunction(this.itemClass) ? this.itemClass(item) : this.itemClass,
                {
                    clickable: !!this.$listeners['dblclick:row'],
                },
            ];
        },
    },
}
</script>

<template>
    <v-data-table
        v-bind="$attrs"
        v-on="$listeners"
        :headers="headersWithActions"
        :search="searchLocal"
        :sort-by.sync="sortByLocal"
        :sort-desc.sync="sortDescLocal"
        :footer-props="footerProps"
        :items="filterLocal ? filteredItems : items"
        :item-class="getItemClass"
        :class="{'v-data-table--items-per-page-hidden': isItemsPerPageHidden}"
        hide-default-header
    >
        <template v-slot:footer.prepend>
            <v-btn
                v-if="!!$listeners['click:add'] && permissions.canCreate"
                @click="$emit('click:add')"
                color="primary"
            >
                Добавить
            </v-btn>
        </template>

        <template v-slot:top>
            <template v-if="withToolbar">
                <!--
                    @link https://v2.vuetifyjs.com/en/components/toolbars/
                -->
                <v-toolbar flat>
                    <v-toolbar-title>{{ heading }}</v-toolbar-title>
                    <v-spacer></v-spacer>

                    <v-icon-tooltip
                        v-if="toolbarAdd && !!$listeners['click:add'] && permissions.canCreate"
                        @click="$emit('click:add')"
                        icon="mdi-plus"
                    >
                        Добавить
                    </v-icon-tooltip>

                    <v-icon-tooltip
                        v-if="clearSort || !sortByDefault"
                        :disabled="isNoSort"
                        @click="sortByLocal = []; sortDescLocal = []"
                        icon="mdi-sort-variant-remove"
                    >
                        Сбросить сортировку
                    </v-icon-tooltip>
                    <v-icon-tooltip
                        v-if="sortByDefault"
                        :disabled="isDefaultSort"
                        @click="sortByLocal = sortByDefault; sortDescLocal = sortDescDefault"
                        icon="mdi-sort-variant"
                    >
                        Сортировка по умолчанию
                    </v-icon-tooltip>
                    <v-icon-tooltip
                        v-if="searchEnable"
                        @click="searchActive = !searchActive"
                        :icon="searchActive ? 'mdi-magnify-remove-outline' : 'mdi-magnify'"
                    >
                        Поиск
                    </v-icon-tooltip>
                    <v-icon-tooltip
                        v-if="filter"
                        @click="filterLocal = !filterLocal"
                        :icon="filterLocal ? 'mdi-filter-remove' : 'mdi-filter'"
                    >
                        Фильтры
                    </v-icon-tooltip>
                    <v-icon-tooltip
                        v-if="!!$listeners['click:reload']"
                        @click="$emit('click:reload')"
                        icon="mdi-reload"
                    >
                        Обновить
                    </v-icon-tooltip>
                </v-toolbar>
                <v-divider></v-divider>
            </template>
            <slot name="top.append"></slot>
            <v-text-field
                v-if="searchActive"
                v-model="searchLocal"
                prepend-inner-icon="mdi-magnify"
                clearable
                label="Поиск"
                hide-details
                class="pt-4 px-4"
            ></v-text-field>
        </template>

        <template v-slot:header="{props, on}" v-if="!hideDefaultHeader">
            <v-data-table-custom-header
                v-if="filterLocal"
                v-bind="props"
                v-on="on"
                v-model="filters"
                :headers="props.headers"
                :items="items"
                :filtered-items.sync="filteredItems"
            ></v-data-table-custom-header>
            <v-data-table-header
                v-bind="props"
                v-on="on"
                :headers="props.headers"
            >
                <template v-for="headerWithTooltip in headersWithTooltip" v-slot:[headerWithTooltip]="{header}">
                    <v-tooltip
                        :key="headerWithTooltip"
                        top
                    >
                        <template v-slot:activator="{on}">
                            <span v-on="on" v-html="header.text"></span>
                        </template>
                        {{header.tooltip}}
                    </v-tooltip>
                </template>
            </v-data-table-header>
        </template>

        <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
            <slot :name="slot" v-bind="scope"/>
        </template>

        <template v-for="(column) in ['created_at', 'updated_at']" v-slot:[`item.${column}`]="{ value }">
            <div class="datetime" :key="column">
                <div class="date">{{ value | moment('DD.MM.Y') }}</div>
                <div class="time">{{ value | moment('HH:mm:ss') }}</div>
            </div>
        </template>

        <template v-slot:item.approve_mask="{value}">
            <div v-html="getBusinessProcessState(value)"></div>
        </template>

        <template v-slot:item.language="{value}">
            <v-icon-flag :value="value" width="16"></v-icon-flag>
        </template>

        <template v-slot:item.actions="{item}">
            <v-icon
                v-if="!!$listeners['click:edit'] && permissions.canUpdate"
                small
                @click.stop="$emit('click:edit', item)"
                :disabled="!canEdit(item)"
            >
                mdi-pencil
            </v-icon>
            <v-icon
                v-if="!!$listeners['click:delete'] && permissions.canDelete"
                small
                @click.stop="$emit('click:delete', item)"
                :disabled="!canDelete(item)"
            >
                mdi-delete
            </v-icon>
        </template>

        <template v-slot:footer>
            <slot name="footer" v-bind="{permissions}"></slot>
        </template>

    </v-data-table>
</template>

<style scoped lang="css">
>>> table tbody tr.clickable {
    cursor: pointer;
}

div.v-data-table.v-data-table--items-per-page-hidden >>> .v-data-footer {
    padding-top: 11px;
    padding-bottom: 11px;
}

div.v-data-table.v-data-table--items-per-page-hidden >>> .v-data-footer__pagination {
    margin-left: auto;
}

>>> .v-menu.v-small-dialog {
    display: none;
}

>>> .v-small-dialog__activator {
    display: inline;
}

>>> .v-data-table-header tr th {
    white-space: nowrap;
}

>>> .v-data-table-header tr th span {
    white-space: normal;
}

>>> .datetime {}
>>> .datetime div { white-space: nowrap; line-height: 1.1; }
>>> .datetime .date {}
>>> .datetime .time { font-size: 12px; opacity: 0.9; }

</style>
